"use client"

import { useCallback, useId, useLayoutEffect, useMemo, useState, type RefObject } from "react"
import { usePathname, useRouter } from "next/navigation"
import { Building2, CarFront, ChevronDown, Crown, FileText, Headset, LayoutDashboard, WalletCards, type LucideIcon } from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/panel/dropdown-menu"
import { filterCrmNavigation, filterMyNavigation, navigationFor, panelPage, type Panel, type PanelPage } from "./navigation"
import { useWorkspaceSession } from "./session"
import { panelNavigationTarget } from "./navigation-target"
import { isSettingsPanelSwitch } from "./settings-navigation"
import { companyNavigationTarget } from "./company-navigation"
import { useCompanySelection } from "./use-company-selection"
import { customerNavigationBindings } from "@/components/layouts/customer/navigation-slots"

export function usePanelNavigation(panel: Panel) {
  const pathname = usePathname()
  const router = useRouter()
  const { session } = useWorkspaceSession(panel)
  const [companyId] = useCompanySelection(panel==="my"?session?.user.id:undefined)
  const identity = useMemo(() => panel === "crm" && session?.role ? { role: session.role, permissions: session.permissions } : undefined, [panel, session])
  const navigation = useMemo(() => {
    if(panel==="crm")return identity?filterCrmNavigation(identity):[]
    if(!session)return navigationFor(panel)
    const selected=session.organizations?.find(item=>item.id===companyId)
    return filterMyNavigation(selected?[selected.role]:session.organizations?.map(item=>item.role)??[])
  },[panel,identity,session,companyId])
  const state = useMemo(() => {
    const current = panelPage(panel, pathname, identity) ?? panelPage(panel, "/", identity) ?? panelPage(panel, "/")!
    if(panel==="my"&&session&&current.group.id==="company")return {...current,group:navigation.find(section=>section.id==="company")??{...current.group,pages:[]}}
    return panel === "crm" && !identity ? { ...current, group: { ...current.group, pages: [] } } : current
  }, [panel, pathname, identity,session,navigation])
  const navigate = useCallback((path: string, options?: { skipUnsavedPrompt?: boolean }) => {
    const baseTarget = panelNavigationTarget(panel, window.location.pathname, path)
    const target = panel==="my" ? companyNavigationTarget(baseTarget,companyId) : baseTarget
    if(panel==="crm"&&!window.dispatchEvent(new CustomEvent("crm-open-document",{cancelable:true,detail:{path:target,skipUnsavedPrompt:options?.skipUnsavedPrompt}})))return
    if (target === window.location.pathname + window.location.search + window.location.hash) return
    if (!options?.skipUnsavedPrompt && !window.dispatchEvent(new CustomEvent(`${panel}-before-navigate`, { cancelable: true, detail: { path: target } }))) return
    if (panel === "crm" && isSettingsPanelSwitch(window.location.pathname, target)) { window.history.pushState(null, "", target); return }
    router.push(target)
  }, [panel, router,companyId])
  return { ...state, session, navigation, navigate, navigationReady: panel !== "crm" || !!session?.role }
}

export function PanelOverflowMenu({ pages, activePath, onNavigate }: { pages: PanelPage[]; activePath: string; onNavigate: (path: string) => void }) {
  const triggerId = `panel-more-pages-${useId()}`
  return <DropdownMenu><DropdownMenuTrigger id={triggerId} aria-label="More pages" className="absolute right-1 top-1/2 z-10 grid size-7 -translate-y-1/2 place-items-center rounded-full text-(--shell-ink) hover:bg-white/10"><ChevronDown className="size-4" /></DropdownMenuTrigger>
    <DropdownMenuContent side="bottom" align="end" className="min-w-48"><DropdownMenuGroup>{pages.map(page => <DropdownMenuItem key={page.path} onClick={() => onNavigate(page.path)} aria-current={activePath === page.path ? "page" : undefined}>{page.label}</DropdownMenuItem>)}</DropdownMenuGroup></DropdownMenuContent>
  </DropdownMenu>
}

export function useMyPanelMenus(rootRef: RefObject<HTMLElement | null>, enabled: boolean, htmlVersion: string, tabStates?: string[]) {
  const state = usePanelNavigation("my")
  const [overflowHost, setOverflowHost] = useState<HTMLElement | null>(null)
  const [iconSlots, setIconSlots] = useState<Array<{ host: HTMLElement; Icon: LucideIcon }>>([])
  const [avatarHost, setAvatarHost] = useState<HTMLElement | null>(null)
  const activeIndex = Math.max(0, state.group.pages.findIndex(page => page.id === state.page.id))
  const visiblePages = useMemo(() => state.group.pages.length <= 3 ? state.group.pages : [...state.group.pages.slice(0, 2), state.group.pages[activeIndex > 2 ? activeIndex : 2]], [state.group.pages, activeIndex])

  useLayoutEffect(() => {
    const root = rootRef.current
    if (!enabled || !root) return
    const rail = root.querySelector('nav[aria-label="Fleet sections"]')
    const iconBySection = { overview: LayoutDashboard, trips: CarFront, quotes: FileText, payments: WalletCards, executive: Crown, company: Building2, support: Headset }
    const nextIcons: Array<{ host: HTMLElement; Icon: LucideIcon }> = []
    customerNavigationBindings(root, state.navigation).forEach(({ button, group: section }) => {
      if (!section) { button.hidden = true; return }
      button.hidden = false
      button.setAttribute("aria-label", section.label)
      button.setAttribute("title", section.label)
      button.setAttribute("data-panel-nav-item", "")
      button.setAttribute("data-panel-href", section.pages[0].path)
      const Icon = iconBySection[section.id as keyof typeof iconBySection]
      if (typeof Icon === "function" || typeof Icon === "object") {
        let host = button.querySelector<HTMLElement>('[data-customer-icon-slot]')
        if (!host) { button.querySelector("svg")?.remove(); host = document.createElement("span"); host.dataset.customerIconSlot = ""; host.className = "inline-flex"; button.prepend(host) }
        nextIcons.push({ host, Icon })
      }
      if (state.group.id === section.id) button.setAttribute("aria-current", "page")
      else button.removeAttribute("aria-current")
      button.classList.toggle("text-(--shell-ink)", state.group.id === section.id)
      button.classList.toggle("text-(--shell-ink-icon)", state.group.id !== section.id)
      button.classList.toggle("rounded-full", state.group.id === section.id)
      button.classList.toggle("rounded-lg", state.group.id !== section.id)
      button.classList.toggle("bg-white/10", state.group.id === section.id)
      button.classList.toggle("shadow-(--shell-rim)", state.group.id === section.id)
    })
    setIconSlots(previous => previous.length === nextIcons.length && previous.every((item,index) => item.host === nextIcons[index].host && item.Icon === nextIcons[index].Icon) ? previous : nextIcons)
    const avatar = rail?.querySelector<HTMLElement>('button[aria-label="Operator menu"] [data-slot="avatar"]')
    if (avatar) {
      let host = avatar.querySelector<HTMLElement>(":scope > [data-customer-avatar-host]")
      if (!host) {
        host = document.createElement("span")
        host.dataset.customerAvatarHost = ""
        host.style.display = "contents"
        // React owns the portrait; no captured image remains to restore a stale URL.
        avatar.replaceChildren(host)
      }
      setAvatarHost(current => current === host ? current : host)
    }
    const list = root.querySelector<HTMLElement>('[role="tablist"]')
    if (list?.parentElement) list.parentElement.dataset.customerTabCount = String(visiblePages.length)
    const tabs = Array.from(list?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [])
    tabs.forEach((tab, index) => {
      const page = visiblePages[index]
      if (!page) { tab.hidden = true; return }
      tab.hidden = false
      const label = tab.querySelector("span")
      if (label) label.textContent = page.label
      tab.setAttribute("data-panel-href", page.path)
      tab.setAttribute("data-panel-page", page.path)
      label?.setAttribute("data-section-label", "")
      const selected = page.id === state.page.id
      tab.setAttribute("aria-selected", String(selected))
      tab.toggleAttribute("data-active", selected)
      tab.tabIndex = selected ? 0 : -1
    })
    const content = root.querySelector<HTMLElement>('[role="tabpanel"]')
    const currentTab = tabs.find(tab => tab.getAttribute("aria-selected") === "true")
    if (content && currentTab) {
      if (currentTab.id) content.setAttribute("aria-labelledby", currentTab.id)
      tabs.forEach(tab => { if (content.id) tab.setAttribute("aria-controls", content.id) })
    }
    const slot = Math.max(0, visiblePages.findIndex(page => page.id === state.page.id))
    if (list && tabStates?.[slot]) {
      const template = document.createElement("div")
      template.innerHTML = tabStates[slot]
      const referenceList = template.querySelector('[role="tablist"]')
      const currentGraphic = list.parentElement?.querySelector(':scope > svg')
      const sourceGraphic = referenceList?.parentElement?.querySelector(':scope > svg')
      const currentBackdrop = list.parentElement?.querySelector(':scope > div[aria-hidden="true"]')
      const sourceBackdrop = referenceList?.parentElement?.querySelector(':scope > div[aria-hidden="true"]')
      if (currentGraphic && sourceGraphic && currentGraphic.innerHTML !== sourceGraphic.innerHTML) currentGraphic.innerHTML = sourceGraphic.innerHTML
      if (currentBackdrop && sourceBackdrop && currentBackdrop.innerHTML !== sourceBackdrop.innerHTML) currentBackdrop.innerHTML = sourceBackdrop.innerHTML
    }
    setOverflowHost(state.group.pages.length > 3 ? list : null)
  }, [rootRef, enabled, htmlVersion, state.group.id, state.group.pages, state.page.id, state.page.path, state.session, state.navigation, tabStates, visiblePages])

  return { ...state, iconSlots, avatarHost, overflowHost, overflowPages: state.group.pages.filter(page => !visiblePages.some(visible => visible.path === page.path)) }
}
